import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import EditableGridForm from '../../../BillingAndPayment/EditableGridForm';
import { authenticatedFetch } from '../../lib/api';
import { toast } from 'react-toastify';

interface ResourceManagerProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    columns?: any[];
    endpoint?: string;
    filters?: Record<string, any>;
    basePayload?: Record<string, any>;
    onSuccess?: () => void;
    // Manual controls (optional if endpoint is provided)
    data?: any[];
    isLoading?: boolean;
    actions?: {
        onCreate: (newItem: any) => Promise<void>;
        onUpdate: (id: string | number, updates: any) => Promise<void>;
        onDelete: (id: string | number) => Promise<void>;
    };
    addButtonLabel?: string;
    showConfirmModal?: (props: any) => void;
    className?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    defaultRow?: Record<string, any>;
    autoAdd?: boolean;
}

const sizeClasses = {
    sm: 'tablet:!w-[30%] desktop:!w-[25%] !max-w-[400px]',
    md: 'tablet:!w-[50%] desktop:!w-[40%] !max-w-[700px]',
    lg: 'tablet:!w-[70%] desktop:!w-[60%] !max-w-[900px]',
    xl: 'tablet:!w-[90%] desktop:!w-[80%] !max-w-[1200px]',
};

export const ResourceManager: React.FC<ResourceManagerProps> = ({
    isOpen,
    onClose,
    title,
    description,
    data: manualData,
    columns = [{ field: 'display_name', header: 'Name', required: true, width: '100%', type: 'text' }],
    isLoading: manualLoading,
    endpoint,
    filters,
    basePayload,
    onSuccess,
    actions: manualActions,
    addButtonLabel,
    showConfirmModal,
    className,
    size = 'md',
    defaultRow,
    autoAdd,
}) => {
    const [internalData, setInternalData] = useState<any[]>([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const loadData = useCallback(async () => {
        if (!endpoint) return;
        setInternalLoading(true);
        try {
            let url = endpoint;
            if (filters) {
                const params = new URLSearchParams();
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        params.append(key, String(value));
                    }
                });
                const queryString = params.toString();
                if (queryString) {
                    url += (url.includes('?') ? '&' : '?') + queryString;
                }
            }

            const res = await authenticatedFetch(url);
            // Support both { data: [...] } and direct array responses
            const list = Array.isArray(res) ? res : (res.data || []);
            setInternalData(list);
        } catch (e) {
            console.error(e);
        } finally {
            setInternalLoading(false);
        }
    }, [endpoint, JSON.stringify(filters)]);

    useEffect(() => {
        if (isOpen && endpoint) {
            loadData();
        }
    }, [isOpen, endpoint, loadData]);

    const handleCreate = async (newItem: any) => {
        if (endpoint) {
            try {
                const payload = { ...basePayload, ...newItem };
                const res = await authenticatedFetch(endpoint, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
                toast.success('Added successfully');
                loadData();
                onSuccess?.();
                return res;
            } catch (e: any) {
                const errorsFirst = Array.isArray(e?.errors) && e.errors.length > 0 ? String(e.errors[0]) : undefined;
                toast.error(errorsFirst || e?.detail || e?.message || 'Failed to add');
                throw e;
            }
        }
        if (manualActions?.onCreate) {
            return manualActions.onCreate(newItem);
        }
    };

    const handleUpdate = async (id: string | number, updates: any) => {
        if (endpoint) {
            try {
                const payload = { ...basePayload, ...updates };
                await authenticatedFetch(`${endpoint}/${id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(payload),
                });
                toast.success('Updated successfully');
                loadData();
                onSuccess?.();
            } catch (e: any) {
                const errorsFirst = Array.isArray(e?.errors) && e.errors.length > 0 ? String(e.errors[0]) : undefined;
                toast.error(errorsFirst || e?.detail || e?.message || 'Failed to update');
                throw e;
            }
            return;
        }
        return manualActions?.onUpdate(id, updates);
    };

    const handleDelete = async (id: string | number) => {
        if (endpoint) {
            try {
                await authenticatedFetch(`${endpoint}/${id}`, { method: 'DELETE' });
                toast.success('Deleted successfully');
                loadData();
                onSuccess?.();
            } catch (e: any) {
                const errorsFirst = Array.isArray(e?.errors) && e.errors.length > 0 ? String(e.errors[0]) : undefined;
                toast.error(errorsFirst || e?.detail || e?.message || 'Failed to delete');
                throw e;
            }
            return;
        }
        return manualActions?.onDelete(id);
    };

    if (!isOpen) return null;

    const displayData = endpoint ? internalData : (manualData || []);
    const isLoading = endpoint ? internalLoading : manualLoading;

    const filteredData = searchValue
        ? displayData.filter((item: any) =>
            Object.values(item).some(
                (val) =>
                    val &&
                    val.toString().toLowerCase().includes(searchValue.toLowerCase())
            )
        )
        : displayData;

    return (
        <Modal open={true}
            className={`${sizeClasses[size]} !rounded-2xl overflow-hidden shadow-2xl ${className || ''}`}
            title={title}
            onClose={onClose}
        >
            <div className="space-y-4">
                {/* Header Section */}
                <div className="flex flex-col gap-2">
                    {description && <p className="text-sm text-gray-600">{description}</p>}

                    {/* Search Input */}
                    <div className="relative group">
                        <input
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="Search items..."
                            className="w-full bg-slate-50/50 border border-slate-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-teal-primary/10 focus:border-teal-primary outline-none transition-all group-hover:bg-white"
                            aria-label="Search"
                        />
                    </div>
                </div>

                {/* Content Section — EditableGridForm owns its own bounded
                    scroll container by default, so this wrapper only
                    supplies visual chrome; it must not add a second
                    max-h/overflow or the grid's sticky header/add-row would
                    pin to the wrong scrolling ancestor. */}
                <div className="rounded-xl border border-slate-100 bg-white overflow-hidden shadow-sm">
                    {isLoading ? (
                        <div className="py-20 flex justify-center items-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-primary font-bold"></div>
                        </div>
                    ) : (
                        <EditableGridForm
                            columns={columns}
                            data={filteredData}
                            onRowAdd={handleCreate}
                            onRowUpdate={handleUpdate}
                            onRowDelete={handleDelete}
                            showConfirmModal={showConfirmModal}
                            addButtonLabel={addButtonLabel || `Add ${title.replace('Manage ', '')}`}
                            defaultRow={defaultRow}
                            startInAddMode={autoAdd}
                            className="border-0"
                        />
                    )}
                </div>

                {/* Footer Section */}
                <div className="flex justify-end pt-4 border-t border-slate-100">
                    <Button variant="outline" onClick={onClose} className="w-auto px-6">
                        Done
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ResourceManager;
